import type { AxiosResponse } from "axios";
import type { ILightingClassConfig } from "types";
import type { ApiResponse, ResultViewModel, Surface, SidewalkStateItem } from "../types";
import { getSelectedResultIndex, getLumRowCount } from "./result-data";
import { getSelectedRoadClass, isPorCRoadClass, isMRoadClass, getRoadState } from "./road-class";
import { getSidewalkStates } from "./sidewalk";

export function buildResultViewModel(
  data: AxiosResponse<ApiResponse>,
  selectedResult: string | number,
  lumRowStyle: string | undefined,
  lightingClassConfig: ILightingClassConfig
): ResultViewModel | null {
  const responseData = data.data;
  const results = responseData?.result[0]?.result;

  const selectedResultIndex = getSelectedResultIndex(results.result, selectedResult);
  const result = results.result[selectedResultIndex];
  const resultDetails = results.result_details.results[selectedResultIndex];

  if (!result) return null;

  const selectedRoadClass = getSelectedRoadClass(lightingClassConfig);
  const isPorCClass = isPorCRoadClass(selectedRoadClass);
  const isMClass = isMRoadClass(selectedRoadClass);

  const subValResult: SidewalkStateItem[] | undefined = result?.result?.subVal;
  const sidewalkStates = getSidewalkStates(subValResult);

  const subVal: Surface[] =
    (
      JSON.parse((data.config.data as string | undefined) ?? "{}") as {
        request?: { params?: { scene?: { surface?: { surfaces?: Surface[] } } } };
      }
    )?.request?.params?.scene?.surface?.surfaces ?? [];

  const power = result?.config?.power ?? null;
  const delta = String(result?.lum_row?.delta ?? "N/A");
  const num_rows = getLumRowCount(lumRowStyle);
  const loadperkm = delta === "N/A" || power === null ? "N/A" : (1000 / Number.parseFloat(delta)) * num_rows * power;

  return {
    articleNumber: results?.article_number ?? "N/A",
    varNumber: result?.config.ldc_id,
    delta,
    lph: String(result?.lum_row?.lph ?? "N/A"),
    overhang: String(result?.lum_row?.overhang ?? "N/A"),
    tilt: String(result?.lum_row?.tilt ?? "N/A"),
    lvkPngUrl: result?.config?.lvk_png_url ?? null, // @deprecated
    lvkImgUrl: result?.config?.lvk_img_url ?? null,
    pdfUrl: result?.config?.pdf_url ?? "",
    lampset: result?.config?.lampset_name ?? "",
    lightFlux: String(result?.config?.light_flux ?? "N/A"),
    power,
    nLums: result?.n_lums ?? null,
    loadperkm,
    recadzLink: result?.config?.recadz_url ?? "",
    ldtLink: result?.config?.ldt_url ?? "",
    requestHash: results.request_hash,
    ulor: result?.result?.ulor ?? -1,
    fTi: result?.result?.f_ti ?? -1,
    article: results?.article_number,
    luminance: resultDetails?.luminance ?? null,
    luminanceSummary: result?.result ?? null,
    illuminance: resultDetails?.illuminance ?? null,
    displayIlluminance: isPorCClass,
    displayLuminance: isMClass,
    surfaces: resultDetails?.surfaces ?? null,
    subVal,
    subValResult,
    sState: getRoadState(result, isPorCClass, isMClass),
    eStateLeft: sidewalkStates.e_state_left,
    eStateRight: sidewalkStates.e_state_right,
  };
}
